import { useState } from 'react';

import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Card,
  DateInput,
  DatePicker,
  FormField,
  FormHelp,
  Heading,
  Label,
  Modal,
  Paragraph,
  Text,
  Textarea,
  TextButton,
} from '@zillow/constellation-10';

import { getBooleanConfig } from '@zg-rentals/frontend-config/client';

import { formatDate } from '@common/paymentsUtils/dateUtils';

type Props = {
  rentMonthHeading: string;
  dueDate: string;
  tenantName: string;
  amount: string;
};

const MarkAsPaidModalContent = ({
  rentMonthHeading,
  dueDate,
  tenantName,
  amount,
  onClose,
}: Props & { onClose: () => void }) => {
  const [note, setNote] = useState('');
  const [dateReceived, setDateReceived] = useState('');
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const handleSubmit = () => {
    // TODO(TM-1519): wire MarkPaymentAsPaidOfflineWebApiHandler_markPaymentAsPaidOffline
    // once getTransactions returns rentTermsKey for past-due rows. Submit the collected
    // { dateReceived, note } with rentTermsKey + resourceId (propertyId) here.
    onClose();
  };

  return (
    <Modal
      onOpenChange={(isOpen) => !isOpen && onClose()}
      open={true}
      size={{ base: 'fullScreen', md: 'md' }}
      header={<Heading level={2}>Mark as paid</Heading>}
      body={
        <>
          <Paragraph css={{ marginBottom: 'default' }}>
            {`Add a payment you received outside Zillow. Once recorded, it will be added to your transaction history and your renter’s past-due status will clear automatically.`}
          </Paragraph>

          <Card elevated={false} css={{ marginBottom: 'default' }}>
            <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'default' }}>
              <Box css={{ display: 'flex', flexDirection: 'column' }}>
                <Text textStyle="body-sm-bold">{rentMonthHeading}</Text>
                <Text textStyle="body-sm">{`Due ${formatDate(dueDate, 'MMM d, yyyy')} · ${tenantName}`}</Text>
              </Box>
              <Text textStyle="body-sm-bold">{amount}</Text>
            </Box>
          </Card>

          <FormField
            css={{ marginBottom: 'default' }}
            required
            label={<Label>Date received</Label>}
            description={<FormHelp>MM/DD/YYYY</FormHelp>}
            control={
              <DatePicker
                closeModalOnDateSelect
                dateFormat="MM/dd/yyyy"
                open={isCalendarOpen}
                onOpenChange={setIsCalendarOpen}
                value={dateReceived}
                onValueChange={(value) => setDateReceived(String(value))}
                input={
                  <DateInput
                    css={{ paddingLeft: 'default' }}
                    value={dateReceived}
                    readOnly
                    onClick={() => setIsCalendarOpen(true)}
                  />
                }
              />
            }
          />

          <FormField
            css={{ marginBottom: 'default' }}
            optional
            label={<Label>Note</Label>}
            control={<Textarea value={note} onChange={(event) => setNote(event.target.value)} />}
          />

          <Alert tone="warning">
            You’ll see this payment on your Zillow dashboard, but it won’t appear on your Stripe-issued 1099.
          </Alert>
        </>
      }
      footer={
        <ButtonGroup aria-label="Mark as paid actions">
          <TextButton onClick={onClose}>Cancel</TextButton>

          <Button tone="brand" emphasis="filled" onClick={handleSubmit}>
            Record payment
          </Button>
        </ButtonGroup>
      }
    />
  );
};

const MarkAsPaidAction = (props: Props) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!getBooleanConfig('isLandlordMarkAsPaidOfflineEnabled', false)) {
    return null;
  }

  return (
    <>
      <TextButton onClick={() => setIsOpen(true)}>Mark as paid</TextButton>
      {isOpen && <MarkAsPaidModalContent {...props} onClose={() => setIsOpen(false)} />}
    </>
  );
};

export default MarkAsPaidAction;
